import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { untilDestroyed } from '$core/operators/until-destroyed.operator';
import {
  Batch,
  BatchMailing,
  BatchMailingStatus,
  BatchStatusEnum,
  DepartureAttachmentItem,
  PartialReceiptsItem,
  PartialReceiptsItemStatus,
} from '../../models';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { BatchesService, BatchesParseStorageService } from '../../services';
import * as dayjs from 'dayjs';
import { NOTIFICATIONS_MAP } from '$modules/personal/modules/batch';
import { filter, finalize, first, switchMap } from 'rxjs/operators';
import { faSyncAlt, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { ConfirmDeleteModalComponent } from '$core/modal/confirm-delete/confirm-delete.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { BlankStatusType } from '$modules/personal/modules/batch/features/blanks/blanks.metadata';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { validateLocalStoragePerPage } from '$modules/personal/modules/batch/validateLocalStorageBatch';
import { checkPhonePrefix } from '$shared/utils';
import { ConfigService } from '$core/services/config.service';

type ConvertedAttachments = {
  [key in keyof PartialReceiptsItemStatus]: PartialReceiptsItem[];
};

@Component({
  selector: 'app-batch-mailing-card',
  templateUrl: './batch-mailing-card.component.html',
  styleUrls: ['./batch-mailing-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatchMailingCardComponent implements OnInit, OnDestroy {
  private readonly config = inject(ConfigService);

  public readonly BatchMailingStatus = BatchMailingStatus;
  public readonly checkPhonePrefix = checkPhonePrefix;
  public readonly NOTIFICATIONS_MAP = NOTIFICATIONS_MAP;
  public readonly dayjs = dayjs;
  public readonly BatchStatusEnum = BatchStatusEnum;
  public readonly faTrashAlt = faTrashAlt;
  public readonly faSyncAlt = faSyncAlt;
  public readonly Object = Object;
  public readonly faCopy = faCopy;

  public batch: Batch;
  public batchDeparture = signal<BatchMailing | null>(null);
  public isLoading$ = new BehaviorSubject(false);
  public isTrackLoading$ = new BehaviorSubject(false);
  public showAllSteps = false;
  public isFullScreen$: BehaviorSubject<boolean> =
    this.batchStorageService.isFullScreen$;
  public activeTab = 1;
  public convertedAttachments: ConvertedAttachments =
    {} as ConvertedAttachments;
  public departureBlankStatus$ = new BehaviorSubject<BlankStatusType>('new');

  public get isELS() {
    return this.batch.payment_type === 'electronic_personal_account';
  }

  @HostListener('document:keydown.escape')
  public onEscapeKeyPress() {
    if (this.isFullScreen$.getValue()) {
      this.toggleFullScreen();
    }
  }

  /**
   * Computed mailing recipient name to display.
   */
  protected readonly recipientName = computed(() => {
    const mailing = this.batchDeparture();

    if (!mailing || !mailing.recipient_contact_widget_data.person) {
      return '';
    }

    const person = mailing.recipient_contact_widget_data.person;

    switch (person.type) {
      case 'individual':
        return (
          [person.last_name, person.first_name, person.second_name]
            .filter(Boolean)
            .join(' ') || '-'
        );

      default:
        return person.organization_name || '-';
    }
  });

  /**
   * Ops URL for redirect.
   */
  protected readonly opsUrl = `/${this.config.urlByService('postoffice')}/`;

  constructor(
    private readonly batchesService: BatchesService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly modalService: BsModalService,
    private readonly translateService: TranslateService,
    private readonly batchStorageService: BatchesParseStorageService,
    private readonly renderer: Renderer2,
    private readonly toastr: ToastrService
  ) {}

  public ngOnInit() {
    this.loadBatchAndDeparture();
  }

  public navigateToViewBatch() {
    let perPage = 15;

    if (localStorage.getItem('batch-departures-per-page')) {
      perPage = validateLocalStoragePerPage('batch-departures-per-page');
    }

    const batchId = this.activatedRoute.snapshot.params['id'];
    const mailingId = this.batchDeparture()?.id;

    return this.router.navigate(
      [`personal/batch/batches/${batchId}/view-batch`],
      {
        queryParams: {
          departureId: mailingId,
          perPage,
        },
      }
    );
  }

  public navigateToEditDeparturePage() {
    const id = this.activatedRoute.snapshot.params['id'];
    const mailingId = this.batchDeparture()?.id;

    return this.router.navigate([`personal/batch/batches/${id}/edit`], {
      queryParams: {
        departureId: mailingId,
      },
    });
  }

  public confirmDeleteDeparture() {
    const modalRef = this.modalService.show(ConfirmDeleteModalComponent, {
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered',
      initialState: {
        title: this.translateService.instant(
          'BATCH_MAILING.BATCHES_TAB.ACTIONS.DELETE_DEPARTURE'
        ),
        subTitle: this.translateService.instant(
          'BATCH_MAILING.BATCHES_TAB.ACTIONS.CONFIRM_DELETING_DEPARTURE'
        ),
        submitBtnClass: 'button--filled',
        cancelBtnClass: 'button--text',
      },
    });

    modalRef.onHidden
      .pipe(
        first(),
        filter((res) => res === 'delete')
      )
      .subscribe({
        next: () => this.deleteDeparture(),
      });
  }

  public reloadTrack(batchDeparture: BatchMailing) {
    this.isTrackLoading$.next(true);
    this.batchesService
      .updateTracking(batchDeparture.s10code)
      .pipe(
        finalize(() => this.isTrackLoading$.next(false)),
        untilDestroyed(this)
      )
      .subscribe({
        next: (updatedSteps) => {
          const initialBatchDeparture = this.batchDeparture();

          if (!initialBatchDeparture) {
            return;
          }

          this.batchDeparture.set({
            ...initialBatchDeparture,
            tracking_code: {
              ...(initialBatchDeparture.tracking_code || {}),
              steps: updatedSteps,
            },
          } as BatchMailing);
        },
      });
  }

  public onShowMoreClick() {
    this.showAllSteps = !this.showAllSteps;
  }

  public toggleFullScreen() {
    this.batchStorageService.toggleFullScreen(this.renderer);
  }

  public setActiveTab(number: number) {
    this.activeTab = number;
  }

  public copyToClipboard(s10code: string) {
    navigator.clipboard.writeText(s10code).then(() => {
      this.toastr.success(
        this.translateService.instant(
          'BATCH_MAILING.BATCHES_TAB.DEPARTURE_CARD.BARCODE_COPIED'
        ),
        s10code
      );
    });
  }

  public ngOnDestroy(): void {}

  private loadBatchAndDeparture(): void {
    const batchId = this.activatedRoute.snapshot.params['id'];
    const mailingId = this.activatedRoute.snapshot.queryParams['departureId'];
    this.isLoading$.next(true);

    forkJoin({
      batch: this.batchesService.show({ batchId: batchId }),
      batchDeparture: this.batchesService.showMailing({ batchId, mailingId }),
    })
      .pipe(
        switchMap(({ batch, batchDeparture }) => {
          this.batch = batch;
          this.batchDeparture.set(batchDeparture);

          if (batchDeparture.document) {
            this.departureBlankStatus$.next(batchDeparture.document.status);
          }

          if (this.batch.is_partial_receipt) {
            return this.batchesService.getDepartureAttachments(
              batchId,
              mailingId
            );
          }

          return of(null);
        }),
        finalize(() => {
          this.isLoading$.next(false);
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: (departureAttachments: DepartureAttachmentItem[] | null) => {
          if (departureAttachments) {
            this.batchStorageService.batchDepartureAttachments =
              departureAttachments;
            this.convertedAttachments =
              this.convertAttachments(departureAttachments);
          }
        },
      });
  }

  private convertAttachments(
    departureAttachments: DepartureAttachmentItem[]
  ): ConvertedAttachments {
    const result: ConvertedAttachments = {} as ConvertedAttachments;

    departureAttachments.forEach((item) => {
      if (!result[item.status]) {
        result[item.status] = [];
      }
      result[item.status].push(item);
    });

    return result;
  }

  private deleteDeparture() {
    const batchId = this.activatedRoute.snapshot.params['id'];
    const departureId = this.activatedRoute.snapshot.queryParams['departureId'];
    this.isLoading$.next(true);
    this.batchesService
      .removeMailings(batchId, [Number(departureId)])
      .pipe(
        finalize(() => {
          this.isLoading$.next(false);
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: () =>
          this.router.navigateByUrl(
            `personal/batch/batches/${batchId}/view-batch`
          ),
      });
  }
}
